import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig, AxiosInstance } from 'axios';
import { getAuthTokensService } from './AuthTokensService';

interface AuthService {
  refreshToken: () => Promise<unknown>;
  logout: () => Promise<void>;
}

export const setupAuthInterceptor = (
  apiBaseUrl: string,
  _authService: AuthService,
  getQueryParam?: (param: string) => string | null,
  axiosInstance?: AxiosInstance
) => {
  const instance = axiosInstance || axios;
  console.log('Setting up auth interceptor with apiBaseUrl:', apiBaseUrl);

  // Request interceptor
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const tokenStorage = getAuthTokensService();

      const baseURL = config.baseURL || '';
      const url = config.url || '';
      const fullUrl = baseURL + url;

      console.log('Auth interceptor - request:', {
        url,
        baseURL,
        fullUrl,
        includesApiBaseUrl: url.includes(apiBaseUrl),
        includesAnonymous: url.includes('anonymous'),
        apiBaseUrl
      });

      if (!fullUrl.includes(apiBaseUrl) || fullUrl.includes('anonymous')) {
        console.log('Auth interceptor - skipping auth header for URL:', fullUrl);
        return config;
      }

      let access_token: string | undefined = tokenStorage.getCurrentCredentials()?.access_token?.token;

      // Handle reset-password and confirm token scenarios
      if (
        (window.location.href.includes('reset-password') ||
          window.location.href.includes('confirm')) &&
        window.location.href.includes('token') &&
        getQueryParam
      ) {
        access_token = getQueryParam('token') || undefined;
      }

      console.log('Auth interceptor - adding Authorization header:', {
        hasToken: !!access_token,
        tokenPreview: access_token ? `${access_token.substring(0, 20)}...` : 'none'
      });

      if (access_token) {
        config.headers.Authorization = `Bearer ${access_token}`;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as RetriableRequest | undefined;

      // The refresh call's own failure is handled where it is made.
      if (!originalRequest || isRefreshCall(originalRequest.url)) {
        return Promise.reject(error);
      }

      if (error.response?.status === 401) {
        if (!originalRequest.url?.includes('/login') && !originalRequest._retry) {
          return handle401Error(error, originalRequest, getAuthTokensService(), instance);
        }
        // Already refreshed for this request: the caller and the AuthGuard handle it.
        return Promise.reject(error);
      }

      // A request that got no response (network drop, sleep, rollout) is a
      // warning: the caller decides whether it's an error, and the case
      // editor's polls back off on it instead of logging every tick (sc-11869).
      if (error.response) console.error(error);
      else console.warn(error);
      return Promise.reject(error);
    }
  );
};

type RetriableRequest = InternalAxiosRequestConfig & { _retry?: boolean };
type TokenStorage = ReturnType<typeof getAuthTokensService>;

// One refresh at a time: 401s that arrive while it runs wait for its token.
let refreshInFlight: Promise<string | null> | null = null;
// Tokens a refresh issued. Identity minted them with the user's current roles, so
// one refused before it expires means the user lacks access: refreshing again would
// only mint another refused token, and the refetch of /auth/me keyed on each new
// token would loop (sc-11868).
const issuedByRefresh = new Set<string>();
// Tokens identity refused to refresh. If the same token comes back (the parent
// re-posting it), it is signed out again without another round trip.
const refusedTokens = new Set<string>();

/** Test hook: forget the module's refresh state. */
export const resetAuthInterceptorState = () => {
  refreshInFlight = null;
  issuedByRefresh.clear();
  refusedTokens.clear();
};

const isRefreshCall = (url?: string) => !!url && url.includes('auth/refresh');

const bearerOf = (request: InternalAxiosRequestConfig): string | null => {
  const header = request.headers?.Authorization;
  return typeof header === 'string' && header.startsWith('Bearer ') ? header.slice('Bearer '.length) : null;
};

const isExpired = (tokenStorage: TokenStorage, token: string): boolean => {
  try {
    return tokenStorage.decodeToken(token).exp * 1000 <= Date.now();
  } catch {
    // Undecodable: treat as live, so the guard below errs towards no refresh.
    return false;
  }
};

const retryWith = (axiosInstance: AxiosInstance, request: RetriableRequest, token: string) => {
  request._retry = true;
  request.headers.Authorization = `Bearer ${token}`;
  return axiosInstance(request);
};

/**
 * A 401 on an API call. The access token can be refused before it expires: it
 * carries the roles frozen at sign-in, so identity rejects it once an admin changes
 * the user's roles or the session is revoked. Refreshing re-reads the current roles,
 * so each failing request triggers at most one refresh, then is retried once with
 * the new token. When identity refuses the refresh the tokens are cleared and the
 * AuthGuard shows sign-in (no page reload, so a stale token re-posted by the
 * Launchpad parent cannot bounce the app). A refreshed token that is still refused
 * is rejected as is: the user lacks access, and the AuthGuard shows Access required
 * from the refreshed token's roles.
 */
const handle401Error = async (
  error: AxiosError,
  originalRequest: RetriableRequest,
  tokenStorage: TokenStorage,
  axiosInstance: AxiosInstance
): Promise<AxiosResponse> => {
  // Once per request, however it ends (also stops a second registered interceptor).
  originalRequest._retry = true;

  if (refreshInFlight) {
    const token = await refreshInFlight;
    return token ? retryWith(axiosInstance, originalRequest, token) : Promise.reject(error);
  }

  const credentials = tokenStorage.getCurrentCredentials();
  const accessToken = credentials?.access_token?.token;
  const refreshToken = credentials?.refresh_token?.token;
  const sentToken = bearerOf(originalRequest);

  // The token changed after this request went out (a refresh finished, or the
  // parent posted new tokens): retry with the current one instead of refreshing.
  if (accessToken && sentToken && sentToken !== accessToken) {
    return retryWith(axiosInstance, originalRequest, accessToken);
  }

  if (!accessToken || !refreshToken || refusedTokens.has(accessToken)) {
    void tokenStorage.clearTokens();
    return Promise.reject(error);
  }

  if (issuedByRefresh.has(accessToken) && !isExpired(tokenStorage, accessToken)) {
    return Promise.reject(error);
  }

  refreshInFlight = refreshTokens(tokenStorage, axiosInstance, accessToken, refreshToken);
  let token: string | null;
  try {
    token = await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
  return token ? retryWith(axiosInstance, originalRequest, token) : Promise.reject(error);
};

const refreshTokens = async (
  tokenStorage: TokenStorage,
  axiosInstance: AxiosInstance,
  accessToken: string,
  refreshToken: string
): Promise<string | null> => {
  try {
    // Relative path, so the axios instance's baseURL applies.
    const { data } = await axiosInstance.post('auth/refresh', {
      access_token: accessToken,
      refresh_token: refreshToken,
    });
    if (!data?.token) {
      throw new Error('Empty refresh response');
    }
    issuedByRefresh.add(data.token);
    tokenStorage.forceUpdateCredentials({
      access_token: {
        token: data.token,
        roles: data.roles || [],
        tenant_id: data.tenant_id || '',
        exp: data.exp, // ISO string from TokenTemplateDTO
      },
      refresh_token: {
        token: refreshToken,
        exp: tokenStorage.getCurrentCredentials()?.refresh_token?.exp || '',
      },
    });
    return data.token;
  } catch (refreshError: unknown) {
    const status = axios.isAxiosError(refreshError) ? refreshError.response?.status : undefined;
    if (status !== undefined && status !== 401) {
      // The refresh endpoint itself failed (5xx): keep the session, fail this request.
      console.warn('Auth: token refresh failed', status);
      return null;
    }
    // Identity refused: the session is over. Clearing the tokens hands off to the
    // AuthGuard's sign-in page.
    console.info('Auth: session could not be refreshed, signing in again');
    refusedTokens.add(accessToken);
    void tokenStorage.clearTokens();
    return null;
  }
};
